import React, { useEffect, useMemo, useState } from "react";
import { clientInvestSdk } from "@utils/clientInvestSdk";
import { Text } from "@ifgengineering/component-library";
import {
  GlossaryItem as GlossaryItemType,
  GlossaryType,
} from "@ifgengineering/client-invest-sdk";
import ModalWindow from "@components/ModalWindow";
import { logException } from "@helpers/logException/logException";
import { toast } from "react-toastify";
import {
  FormulaContainer,
  GlossaryItemContainer,
  GlossaryListContainer,
  GlossaryModalContainer,
} from "./styled";
import { EisFundInvestmentStatus, StatusLabel } from "@components/EisFund";
import { theme } from "@styles/theme";

interface GlossaryModalProps {
  show: boolean;
  onClose: () => void;
  glossaryType: GlossaryType;
}

const GlossaryModal = ({ glossaryType, show, onClose }: GlossaryModalProps) => {
  const [glossaryItems, setGlossaryItems] = useState<GlossaryItemType[]>([]);

  useEffect(() => {
    const fetchGlossaryItems = async () => {
      try {
        const response = await clientInvestSdk.getGlossary({
          glossaryType,
        });
        setGlossaryItems(response.data.data ?? []);
      } catch (err) {
        toast.error("Failed to fetch Portfolio Glossary. Please try again.");

        // Don't log 401/403 errors - these are expected auth failures
        // handled by the axios interceptor (token refresh/logout)
        const axiosStatus = (err as { response?: { status?: number } })
          ?.response?.status;
        if (axiosStatus !== 401 && axiosStatus !== 403) {
          logException({
            exception: err,
          });
        }
      }
    };

    fetchGlossaryItems();
  }, []);

  return (
    <ModalWindow show={show} onClose={onClose}>
      <GlossaryModalContainer>
        <Text type="T24" color="SLATE900" fontFamily="archiasemibold">
          Glossary
        </Text>
        <GlossaryListContainer>
          {glossaryItems.map((glossaryItem) => (
            <GlossaryItem
              key={glossaryItem.id}
              item={glossaryItem}
              withColors={glossaryType === "EIS_STATUS"}
            />
          ))}
        </GlossaryListContainer>
      </GlossaryModalContainer>
    </ModalWindow>
  );
};

export default GlossaryModal;

interface GlossaryItemProps {
  item: GlossaryItemType;
  withColors: boolean;
}

const GlossaryItem = ({ withColors, item }: GlossaryItemProps) => {
  const statusLabel = useMemo(() => {
    const statusLabelMapping: Record<string, StatusLabel> = {
      "new investment": {
        name: item.term,
        textColor: theme.colors.SLATE800,
        bgColor: theme.colors.SLATE200,
      },
      "breakout potential": {
        name: item.term,
        textColor: theme.colors.SUCCESS800,
        bgColor: theme.colors.SUCCESS100,
      },
      "pre product market fit": {
        name: item.term,
        textColor: theme.colors.INDIGO900,
        bgColor: theme.colors.INDIGO50,
      },
      "success unlikely": {
        name: item.term,
        textColor: theme.colors.CAUTION900,
        bgColor: theme.colors.CAUTION100,
      },
      "write off": {
        name: item.term,
        textColor: theme.colors.ERROR800,
        bgColor: theme.colors.ERROR100,
      },
    };

    const key = item.term
      .replace(/[^a-zA-Z0-9]+/g, " ")
      .trim()
      .toLowerCase();
    return statusLabelMapping[key];
  }, [item]);

  return (
    <GlossaryItemContainer>
      {withColors ? (
        <EisFundInvestmentStatus statusLabel={statusLabel} />
      ) : (
        <Text type="H18" color="SLATE900">
          {item.term}
        </Text>
      )}
      <Text type="P16" color="SLATE800">
        {item.definition}
      </Text>
      {item?.formula && (
        <FormulaContainer>
          <Text type="S14" color="SLATE700">
            {item.formula}
          </Text>
        </FormulaContainer>
      )}
    </GlossaryItemContainer>
  );
};
